<template>
    <div class="service-description-input" :style="style" :class="customClass">
		<input
			type="text"
			:value="modelValue"
			@input="handleInput"
			@focus="handleFocus"
			@blur="handleBlur"
			@keydown.down.prevent="navigateDown"
			@keydown.up.prevent="navigateUp"
			@keydown.enter.prevent="selectHighlightedOption"
			@keydown.esc="hideDropdown"
			:placeholder="placeholder"
			class="field-input"
			ref="inputRef"
      :disabled="disabled"
		/>
		<div v-if="showDropdown && filteredOptions.length > 0" :class="dropdownClasses">
			<ul>
				<li
					v-for="(option, index) in filteredOptions"
					:key="option.id"
					@pointerdown.prevent="selectOption(option)"
					:class="{ 'is-highlighted': index === highlightedIndex }"
					@mouseenter="highlightedIndex = index"
				>
					{{ option.name || `Service ${option.id}` }}
				</li>
			</ul>
		</div>
	</div>
</template>

<script>
export default {
	name: 'ServiceDescriptionInput',
	props: {
		modelValue: { // Free text description
			type: String,
			default: '',
		},
		options: { // Array of { id, name } service objects
			type: Array,
			required: true,
		},
		placeholder: {
			type: String,
			default: 'Enter description',
		},
		includeCustomItems: {
			type: Boolean,
			default: true,
		},
		style: { // Pass through style object
			type: Object,
			default: () => ({})
    },
    customClass: { // Allow parent to pass custom styling hook
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    }
	},
	emits: ['update:modelValue', 'service-selected', 'blur'],
	data() {
		return {
			showDropdown: false,
			highlightedIndex: -1,
			dropdownPosition: 'below', // 'above' or 'below'
			maxDropdownHeight: 200, // Max height in pixels
		};
	},
	computed: {
		extendedOptions() {
			if (!this.includeCustomItems) {
				return this.options;
			}
			// Add custom items that aren't in the catalog
			const customItems = [
				{ id: 'custom-cleaning-materials', name: 'Cost of cleaning materials' }
			];
			return [...this.options, ...customItems];
		},
		
		filteredOptions() {
			if (!this.modelValue || this.modelValue.length < 1) {
				return []; // Don't show options for empty input
			}
			const searchTerm = this.modelValue.toLowerCase();
			return this.extendedOptions.filter(option =>
				option.name?.toLowerCase().includes(searchTerm)
			);
		},
		dropdownClasses() {
			return {
				'dropdown-menu': true,
				'is-above': this.dropdownPosition === 'above',
			};
		}
	},
	methods: {
		// Calculates and returns the optimal position without setting state directly
		getDropdownPosition() {
			if (!this.$refs.inputRef) return 'below'; // Default if input isn't ready

			const inputRect = this.$refs.inputRef.getBoundingClientRect();
			// Find the closest scrollable container or default to window viewport
			const scrollContainer = this.$el.closest('.main-container') || document.documentElement;
			const containerRect = scrollContainer.getBoundingClientRect();

			// Use viewport bottom if container extends beyond viewport
			const spaceBelow = containerRect.bottom - inputRect.bottom;

			// Use viewport top if container extends beyond viewport
			const effectiveContainerTop = Math.max(containerRect.top, 0);
			const spaceAbove = inputRect.top - effectiveContainerTop;

			// Use fixed max height for calculation
			const requiredHeight = this.maxDropdownHeight;
			const minSpaceRequired = 50; // Minimum space needed to consider placing above

			// Prefer below unless space below is insufficient AND space above is sufficient and better
			if (spaceBelow < requiredHeight && spaceAbove >= requiredHeight && spaceAbove > spaceBelow) {
				return 'above';
			} else if (spaceBelow < requiredHeight && spaceAbove >= minSpaceRequired && spaceAbove > spaceBelow) {
				// Fallback: if space above isn't enough for full height, but still better than below
				return 'above';
			} else {
				return 'below';
			}
		},

		// Method to open the dropdown after calculating position
		openDropdown() {
			if (this.filteredOptions.length > 0) {
				this.dropdownPosition = this.getDropdownPosition();
				this.showDropdown = true;
				this.highlightedIndex = -1;
			}
		},

		handleInput(event) {
			const newValue = event.target.value;
			this.$emit('update:modelValue', newValue);
			
			// Show dropdown only if there are matching options
			this.$nextTick(() => {
				if (this.filteredOptions.length > 0) {
					this.openDropdown();
				} else {
					this.hideDropdown();
				}
			});
		},
		
		selectOption(option) {
			this.$emit('update:modelValue', option.name);
			this.$emit('service-selected', option);
			this.hideDropdown();
			this.$refs.inputRef.blur();
		},
		
		hideDropdown() {
			this.showDropdown = false;
			this.highlightedIndex = -1;
		},

		handleBlur(event) {
			this.hideDropdown();
			this.$emit('blur', event);
		},
		
		handleFocus() {
			// Show dropdown only if there are matching options for current input
			if (this.filteredOptions.length > 0) {
				this.openDropdown();
			}
		},
		
		navigateDown() {
			if (!this.showDropdown && this.filteredOptions.length > 0) {
				this.openDropdown();
			} else if (this.showDropdown && this.filteredOptions.length > 0) {
				this.highlightedIndex = (this.highlightedIndex + 1) % this.filteredOptions.length;
				this.scrollToHighlighted();
			}
		},
		
		navigateUp() {
			if (!this.showDropdown) return;
			if (this.filteredOptions.length > 0) {
				this.highlightedIndex = (this.highlightedIndex - 1 + this.filteredOptions.length) % this.filteredOptions.length;
				this.scrollToHighlighted();
			}
		},
		
		selectHighlightedOption() {
			if (this.showDropdown && this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredOptions.length) {
				this.selectOption(this.filteredOptions[this.highlightedIndex]);
			} else {
				this.hideDropdown();
			}
		},
		
		scrollToHighlighted() {
			this.$nextTick(() => {
				const list = this.$el.querySelector('.dropdown-menu ul');
				const item = list?.children[this.highlightedIndex];
				if (item) {
					item.scrollIntoView({ block: 'nearest' });
				}
			});
		}
	}
};
</script>

<style lang="sass" scoped>
@use '@/assets/styles/variables.sass' as variables

.service-description-input
	position: relative
	width: 100%

	.field-input
		width: 100%
		box-sizing: border-box
		padding: 12px // variables.$spacing-xmedium
		border: 1px solid transparent
		background-color: var(--noc-background)
		color: var(--text-color)
		font-size: 14px
		outline: none
		transition: background-color 0.1s ease
		&::placeholder
			color: var(--text-color-secondary)
			opacity: 0.6
		&:hover:not(:disabled)
			background-color: var(--noc-background-secondary)
		&:focus
			border-radius: 0px !important
			border-color: transparent !important

	.dropdown-menu
		position: absolute
		left: 0
		right: 0
		background-color: #ffffff
		border: 1px solid var(--border-color, #d1d5db)
		max-height: 220px
		overflow-y: auto
		z-index: 1000
		top: 100%
		border-radius: 6px
		box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15)
		opacity: 1
		margin-top: 4px
		// Position: above
		&.is-above
			top: auto
			bottom: calc(100% + 4px)
			border-bottom: none
			border-radius: 6px
			margin-top: 0

		ul
			list-style: none
			padding: 4px 0
			margin: 0

		li
			padding: 6px 12px
			cursor: pointer
			font-size: 0.9rem
			color: var(--text-color)
			white-space: nowrap
			overflow: hidden
			text-overflow: ellipsis
			transition: background-color 0.1s ease

			&:hover, &.is-highlighted
				background: var(--noc-blue-05, #eff6ff)
				color: var(--noc-blue, #0b5ae0)
</style>
